import { useEffect, useState } from "react";
import EnvelopeTable from "@/components/tables/EnvelopeTable";
import TableHead from "@/components/tables/TableHead";
import { fetchEnvos } from "@/services/envelope.service";
import { StatusOptions } from "@/utils/constants/constants";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { getListCacheBootstrap, writeListCache } from "@/utils/listPageCache";

type Props = {};

const EnvelopeList = (props: Props) => {
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting, filter, setFilter, searchItem, setSearchItem, resetDataQueries] = useDataQueries();
    const listBootstrap = getListCacheBootstrap("envelopes", { dataLength, sorting, searchItem, filter });

    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [envelopes, setEnvelopes] = useState<any>(listBootstrap.items);
    const [envelopesLeft, setEnvelopesLeft] = useState(true);
    const [envelopesHistory, setEnvelopesHistory] = useState<any>(listBootstrap.items);
    const [allFetched, setAllFetched] = useState(false);
    const [loadData, setLoadData] = useState(!listBootstrap.hasCache);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);

    const handleFetchAllEnvos = () => {
        fetchEnvos(
            pageNo,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                setLoadData(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (response.data?.envelopes?.length < dataLength) {
                    setEnvelopesLeft(false);
                    setAllFetched(true);
                }
                const envelopes = response.data.envelopes;
                setEnvelopes(envelopes);
                setEnvelopesHistory(envelopes);
            },
            (error: any) => {
                console.log(error);
                setLoadData(false);
                setIsLoading(false);
            }
        );
    };
    useEffect(() => {
        const { cacheKey, cached } = getListCacheBootstrap("envelopes", { dataLength, sorting, searchItem, filter });

        setPageNo(1);
        setEnvelopesLeft(true);
        setAllFetched(false);
        if (cached) {
            setEnvelopes(cached.items);
            setEnvelopesHistory(cached.items);
            setTotalCount(cached.totalCount);
            setLoadData(false);
            setIsLoading(false);
        } else {
            setIsLoading(true);
            setLoadData(true);
        }

        fetchEnvos(
            1,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                const nextEnvelopes = response.data.envelopes ?? [];
                writeListCache(cacheKey, nextEnvelopes, response.data.total_count);
                setLoadData(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (nextEnvelopes.length < dataLength) {
                    setEnvelopesLeft(false);
                    setAllFetched(true);
                }
                setEnvelopes(nextEnvelopes);
                setEnvelopesHistory(nextEnvelopes);
            },
            (error: any) => {
                console.log(error);
                setLoadData(false);
                setIsLoading(false);
            }
        );
    }, [dataLength, sorting, searchItem]);

    window.onscroll = function () {
        if (allFetched || isLoading) {
            return;
        }
        if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1) {
            setPageNo(pageNo + 1);
            setIsLoading(true);
            if (envelopesLeft) {
                fetchEnvos(
                    pageNo + 1,
                    dataLength,
                    sorting,
                    filter,
                    searchItem,
                    (response: any) => {
                        setIsLoading(false);
                        setTotalCount(response.data.total_count);
                        if (response.data?.envelopes?.length < dataLength) {
                            setEnvelopesLeft(false);
                            setAllFetched(true);
                        }
                        const sorted_envelopes = response.data.envelopes;
                        const result = [...envelopes, ...sorted_envelopes];
                        setEnvelopes(result);
                        setEnvelopesHistory(result);
                    },
                    (error: any) => {
                        console.log(error);
                        setIsLoading(false);
                    }
                );
            }
        }
    };

    const search = (searchText: string) => {
        setSearchItem(searchText);
    };

    const resetResponse = () => {
        setEnvelopesLeft(true);
        setAllFetched(false);
    };

    const reset = () => {
        resetDataQueries?.();
        resetResponse();
    };

    const handleApplyFilter = () => {
        setPageNo(1);
        resetResponse();
        setLoadData(true);
        setIsLoading(true);
        fetchEnvos(
            1,
            dataLength,
            sorting,
            filter,
            searchItem,
            (response: any) => {
                setLoadData(false);
                setIsLoading(false);
                setTotalCount(response.data.total_count);
                if (response.data?.envelopes?.length < dataLength) {
                    setEnvelopesLeft(false);
                    setAllFetched(true);
                }
                const envelopes = response.data.envelopes;
                setEnvelopes(envelopes);
                setEnvelopesHistory(envelopes);
            },
            (error: any) => {
                console.log(error);
                setLoadData(false);
                setIsLoading(false);
            }
        );
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead id='envelope-table' handleReset={resetResponse} pageNo={pageNo as number} dataLength={dataLength as number} setFilter={setFilter} filter={filter} setDataLength={setDataLength as (dataLength: number) => void} reset={reset} search={search} title="Envelopes" options={StatusOptions} handleApplyFilter={handleApplyFilter} currentEnvo={envelopes.length} totalEnvo={totalCount}/>
            </div>
            <div>
                                <EnvelopeTable
                                    id='envelope-table'
                                    reset={reset}
                                    title="Envelopes"
                                    setEnvelopes={setEnvelopes}
                                    search={search}
                                    isLoading={isLoading}
                                    dataLength={dataLength as number}
                                    sorting={sorting}
                                    setSorting={setSorting as (sorting: any) => void}
                                    searchItem={searchItem as string}
                                    allFetched={allFetched}
                                    envelopes={envelopes}
                                    dataLoad={loadData}
                                    resetDataQueries={resetDataQueries}
                                    setPageNo={setPageNo}
                                />
            </div>
        </div>
    );
};

export default EnvelopeList;
